// @flow

import _ from 'lodash';

import DrugInfoModel, {
  Session_Member_Drug,
  type DrugDoseType,
  type DrugType,
  type SessionMemberDrugType,
} from 'client/models/health/DrugInfoModel';
import { FAMILY_MEMBER_ID_NAME, OTHER } from 'client/constants/health/drug';

import type { IfpAppType } from 'web-core/types/ifp-app';

// Migrated from Angular App
export const DRUG_FORM_MAP = {
  default: {
    singular: 'dose',
    plural: 'doses',
  },
  aerosol: {
    singular: 'inhaler',
    plural: 'inhalers',
  },
  capsule: {
    singular: 'capsule',
    plural: 'capsules',
  },
  tablet: {
    singular: 'tablet',
    plural: 'tablets',
  },
  pellet: {
    singular: 'tablet',
    plural: 'tablets',
  },
  injection: {
    singular: 'injection',
    plural: 'injections',
  },
  cream: {
    singular: 'cream',
    plural: 'creams',
  },
  crystals: {
    singular: 'crystal',
    plural: 'crystals',
  },
  solution: {
    singular: 'solution',
    plural: 'solutions',
  },
  lotion: {
    singular: 'lotion',
    plural: 'lotions',
  },
  liquid: {
    singular: 'liquid',
    plural: 'liquids',
  },
  film: {
    singular: 'film',
    plural: 'films',
  },
  gel: {
    singular: 'gel',
    plural: 'gels',
  },
  granules: {
    singular: 'granule',
    plural: 'granules',
  },
  implant: {
    singular: 'implant',
    plural: 'implants',
  },
  inhaler: {
    singular: 'inhaler',
    plural: 'inhalers',
  },
  kit: {
    singular: 'kit',
    plural: 'kits',
  },
  lozenge: {
    singular: 'lozenge',
    plural: 'lozenges',
  },
  lollipop: {
    singular: 'lollipop',
    plural: 'lollipops',
  },
  nebulizer: {
    singular: 'solution',
    plural: 'solutions',
  },
  oil: {
    singular: 'oil',
    plural: 'oils',
  },
  ointment: {
    singular: 'ointment',
    plural: 'ointments',
  },
  packet: {
    singular: 'packet',
    plural: 'packets',
  },
  pad: {
    singular: 'pad',
    plural: 'pads',
  },
  powder: {
    singular: 'powder',
    plural: 'powders',
  },
  paste: {
    singular: 'paste',
    plural: 'pastes',
  },
  patch: {
    singular: 'patch',
    plural: 'patches',
  },
  insert: {
    singular: 'ring',
    plural: 'rings',
  },
  shampoo: {
    singular: 'shampoo',
    plural: 'shampoo',
  },
  strip: {
    singular: 'strip',
    plural: 'strips',
  },
  suppository: {
    singular: 'suppository',
    plural: 'suppositories',
  },
  suspension: {
    singular: 'canister',
    plural: 'canisters',
  },
  swab: {
    singular: 'swab',
    plural: 'swabs',
  },
  tape: {
    singular: 'tape',
    plural: 'tapes',
  },
};

export const getDrugQuantityOptions = ({
  doseForm,
  qtyDivisible,
}: {
  doseForm: string,
  qtyDivisible: boolean,
}) => {
  const qtyText = DRUG_FORM_MAP[doseForm.toLowerCase()] || DRUG_FORM_MAP.default;
  if (!qtyDivisible) {
    return [
      { value: 1, label: `1 ${qtyText.singular}` },
      { value: 2, label: `2 ${qtyText.plural}` },
      { value: 3, label: `3 ${qtyText.plural}` },
      { value: 4, label: `4 ${qtyText.plural}` },
      { value: 5, label: `5 ${qtyText.plural}` },
      { value: OTHER, label: 'Other' },
    ];
  }
  return [
    { value: 30, label: `30 ${qtyText.plural}` },
    { value: 60, label: `60 ${qtyText.plural}` },
    { value: 90, label: `90 ${qtyText.plural}` },
    { value: OTHER, label: 'Other' },
  ];
};

export const getDrugDurationOptions = (dayText: string) => [
  { value: 7, label: `7 ${dayText}` },
  { value: 15, label: `15 ${dayText}` },
  { value: 30, label: `30 ${dayText}` },
  { value: 60, label: `60 ${dayText}` },
  { value: 90, label: `90 ${dayText}` },
  { value: OTHER, label: 'Other' },
];

type SaveDrugParams = {
  createMemberDrug: Function,
  doseInfo: Array<DrugDoseType>,
  drug: DrugType,
  formData: Object,
  ifpApp: IfpAppType,
  save: Function,
  user: Object,
};

export const saveDrug = ({
  createMemberDrug,
  doseInfo,
  drug,
  formData,
  ifpApp,
  save,
  user,
}: SaveDrugParams) => {
  const { app_id } = ifpApp;
  const { strength } = formData;
  let dose;
  if (doseInfo.length === 1) {
    // Some drugs will only have one or no strength
    // Will default to this if the case
    [dose] = doseInfo;
  } else {
    dose = _.find(doseInfo, { strength });
  }
  const member_id = _.get(formData, FAMILY_MEMBER_ID_NAME, _.get(user, 'primaryMemberId'));
  const drug_json = new DrugInfoModel({ drug, dose, formData });

  if (!user.isLoggedIn) {
    const sessionMemberDrug = new Session_Member_Drug({ drug_json, member_id });
    return save(_.assign({}, user, { drugs: [...user.drugs, sessionMemberDrug] }));
  }
  return createMemberDrug({ app_id, drug_json, member_id });
};

type UpdateDrugParams = {
  doseInfo: Array<DrugDoseType>,
  drug: DrugType,
  formData: Object,
  member_drug_id: string,
  save: Function,
  updateMemberDrug: Function,
  user: Object,
};

export const updateDrug = ({
  doseInfo,
  drug,
  formData,
  member_drug_id,
  save,
  updateMemberDrug,
  user,
}: UpdateDrugParams) => {
  const { strength } = formData;
  let dose;
  if (doseInfo.length === 1) {
    // Some drugs will only have one or no strength
    // Will default to this if the case
    [dose] = doseInfo;
  } else {
    dose = _.find(doseInfo, { strength });
  }
  const member_id = _.get(formData, FAMILY_MEMBER_ID_NAME, _.get(user, 'primaryMemberId'));
  const drug_json = new DrugInfoModel({ drug, dose, formData });

  if (!user.isLoggedIn) {
    const drugs = _.map(user.drugs, (sessionDrug: SessionMemberDrugType) => {
      if (sessionDrug.id === member_drug_id) {
        return {
          ...sessionDrug,
          drug_json,
          member_id,
        };
      }
      return sessionDrug;
    });
    return save(_.assign({}, user, { drugs }));
  }

  return updateMemberDrug({ drug_json, member_drug_id, member_id });
};

type DeleteDrugParams = {
  deleteMemberDrug: Function,
  drug: DrugType,
  fetchIfpApps: Function,
  save: Function,
  user: Object,
};

export const deleteDrug = async ({
  deleteMemberDrug,
  drug,
  fetchIfpApps,
  save,
  user,
}: DeleteDrugParams) => {
  if (!user.isLoggedIn) {
    const { id } = drug;
    const updatedMemberDrugsList = _.filter(
      user.drugs,
      sessionMemberDrug => sessionMemberDrug.id !== id,
    );
    return save(_.assign({}, user, { drugs: updatedMemberDrugsList }));
  }
  await deleteMemberDrug(drug);
  return fetchIfpApps();
};

/* Migrated the following to format member drugs to be used for plan rec search
   {
     dose_id: 13239
     freq: {timePeriodId: 4, intake: 360}
     id: 3376
     fillQty: 30
     ndc: "00071015523"
   }
*/
const timePeriodsOld = ['daily', 'weekly', 'monthly', 'yearly'];
const timePeriodsNew = ['DAY', 'WEEK', 'MONTH', 'YEAR'];

const setTimePeriodId = (drug: Object) => {
  const {
    freq: { timePeriod },
  } = drug;
  if (timePeriodsOld.indexOf(timePeriod) > -1) {
    _.set(drug, 'freq.timePeriodId', parseInt(timePeriodsOld.indexOf(timePeriod) + 1, 10));
  } else if (timePeriodsNew.indexOf(timePeriod) > -1) {
    _.set(drug, 'freq.timePeriodId', parseInt(timePeriodsNew.indexOf(timePeriod) + 1, 10));
  }
};

export const transformDrugsForPlanRec = (drugs: Array<Object>, member_id: string): Array<Object> =>
  _.map(
    _.filter(drugs, drug => drug.member_id === member_id),
    (member_drug) => {
      const drug = member_drug.drug_json;

      // do everything as a per year so it's always an int
      const freq = { timePeriodId: 4 };
      let { fillCount } = drug;

      // for old drugs that don't have a fillCount attached
      if (!fillCount) {
        fillCount = Math.floor(365 / drug.durationDays);
      }

      const count = drug.fillQty * fillCount;

      if (drug.qtyDivisible) {
        _.set(freq, 'intake', count);
      } else {
        _.set(freq, 'fills', count);
      }

      const serverDrug = {
        dose_id: drug.dose.id,
        fillQty: drug.fillQty,
        freq,
        id: drug.id,
        ndc: drug.dose.ndc,
      };

      setTimePeriodId(serverDrug);

      return serverDrug;
    },
  );
